import React from 'react';

export const ServicesSection: React.FC = () => {
  const paintingServices = [
    {
      icon: 'ri-home-4-line',
      title: 'Custom House Painting',
      features: [
        'High-end interior & exterior finishes',
        'Perfect walls, trim, and ceilings',
        'Elegant and modern home painting'
      ]
    },
    {
      icon: 'ri-building-2-line',
      title: 'Commercial Painting (Cut & Roll)',
      features: [
        'Offices, stores, apartments, industrial units',
        'Efficient painting with clean edge detail',
        'Durable coatings for work environments'
      ]
    },
    {
      icon: 'ri-brush-line',
      title: 'Priming & Surface Preparation',
      features: [
        'Patch repairs, sanding, priming',
        'Adhesion & durability first',
        'Best results for repainting & new construction'
      ]
    },
    {
      icon: 'ri-contrast-drop-2-line',
      title: 'Popcorn Ceiling Removal & Skimming',
      features: [
        'Smooth ceilings with modern appeal',
        'Dust controlled service',
        'Painted to a perfect finish'
      ]
    },
    {
      icon: 'ri-spray-line',
      title: 'Spray Painting Cabinets, Metal Doors & Ceilings',
      features: [
        'Factory-grade finish for kitchen cabinets',
        'Rust-protected coatings for metal doors',
        'Spray finish on ceilings and trim'
      ]
    }
  ];

  const flooringServices = [
    {
      icon: 'ri-layout-grid-line',
      title: 'Wood Flooring',
      features: [
        'Elegant hardwood finishes',
        'Smooth installation and leveling',
        'Long-lasting and premium style'
      ],
      image: 'https://static.readdy.ai/image/207d1fdbe5520f856773b12bb4c3bbee/20b010c9c101bc1fcbee07b7dddf944d.jpeg'
    },
    {
      icon: 'ri-layout-masonry-line',
      title: 'Vinyl Flooring',
      features: [
        'Waterproof, modern, and durable',
        'Ideal for basements, kitchens & commercial spaces',
        'Affordable and stylish options'
      ],
      image: 'https://static.readdy.ai/image/207d1fdbe5520f856773b12bb4c3bbee/eb30e7be06804c85770bdaa083d64c0d.jpeg'
    },
    {
      icon: 'ri-stack-line',
      title: 'Carpet Flooring',
      features: [
        'Soft, quiet and comfortable',
        'Suitable for homes, offices & corridors',
        'Clean, precise wall-to-wall fitting'
      ],
      image: 'https://static.readdy.ai/image/207d1fdbe5520f856773b12bb4c3bbee/cd9ffb2405cc58338f9db4ec7b312b53.jpeg'
    }
  ];

  const insulationFeatures = [
    'Wall, ceiling & attic insulation',
    'Energy efficiency upgrades',
    'Soundproofing for commercial & residential units',
    'Reduce heating & cooling costs',
    'Improve comfort & property value'
  ];

  return (
    <section id="services" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-teal-100 rounded-full mb-6">
            <i className="ri-tools-line text-teal-600 mr-2"></i>
            <span className="text-sm font-semibold text-teal-700">Our Services</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Complete Home &amp; Commercial Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From painting to flooring and insulation, we provide comprehensive services for your property
          </p>
        </div>

        {/* Painting Services */}
        <div className="mb-20">
          <div className="flex items-center mb-10">
            <i className="ri-paint-brush-line text-4xl text-teal-600 mr-4"></i>
            <h3 className="text-3xl font-bold text-gray-900">Painting &amp; Surface Finishing</h3>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paintingServices.map((service, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-2xl text-teal-600`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h4>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-600">
                      <i className="ri-check-line text-teal-600 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Flooring Services */}
        <div className="mb-20">
          <div className="flex items-center mb-10">
            <i className="ri-layout-line text-4xl text-teal-600 mr-4"></i>
            <h3 className="text-3xl font-bold text-gray-900">Flooring Installation</h3>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {flooringServices.map((service, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-full h-64">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-xl text-teal-600`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <i className="ri-check-line text-teal-600 mr-2 mt-0.5 flex-shrink-0"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Insulation Services */}
        <div className="mb-12">
          <div className="flex items-center mb-10">
            <i className="ri-temp-cold-line text-4xl text-teal-600 mr-4"></i>
            <h3 className="text-3xl font-bold text-gray-900">Insulation Services</h3>
          </div>
          
          <div className="bg-gradient-to-br from-teal-50 to-white border border-teal-200 rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-6">
                  Energy Efficiency &amp; Comfort Solutions
                </h4>
                <ul className="space-y-3">
                  {insulationFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-gray-700">
                      <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                      <span className="text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="w-full h-80 rounded-xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20insulation%20installation%20wall%20ceiling%20attic%20energy%20efficiency%20home%20improvement%20thermal%20protection%20soundproofing%20modern%20construction%20quality%20materials%20expert%20installation&width=600&height=500&seq=insulation1&orientation=landscape"
                  alt="Insulation Services"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Premium Materials Section */}
        <div className="bg-gray-900 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-shield-check-line text-3xl text-white"></i>
          </div>
          <h3 className="text-2xl font-bold text-white mb-4">Premium Materials</h3>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            We only use trusted brands like <strong className="text-teal-400">Sherwin-Williams</strong>, <strong className="text-teal-400">Dulux</strong>, <strong className="text-teal-400">Benjamin Moore</strong>, <strong className="text-teal-400">Armstrong</strong>, and <strong className="text-teal-400">Shaw</strong> for paint and flooring durability.
          </p>
        </div>
      </div>
    </section>
  );
};
